import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Shaughnessy = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.shaughnessy-vancouver:About Shaughnessy Paragraph">
          Shaughnessy is one of Vancouver’s most prestigious and historic
          neighborhoods, renowned for its grand estates, lush greenery, and
          exclusive residential feel. Established in 1907 by the Canadian
          Pacific Railway, the area was named after the company’s president,
          Thomas Shaughnessy. Characterized by its winding, tree-lined streets
          and expansive lots, Shaughnessy offers a tranquil retreat from the
          bustling city while remaining centrally located. The neighborhood’s
          rich history is reflected in its well-preserved heritage homes, many
          of which date back to the early 20th century, showcasing architectural
          styles such as Tudor Revival and Colonial.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,200 - $2,500',
          bed2: '$2,900 - $3,800',
          bed3: '$4,500 - $6,000'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.shaughnessy-vancouver:Apartment Hunting Shaughnessy Paragraph 1">
            Finding an apartment for rent in Shaughnessy, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Shaughnessy lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.shaughnessy-vancouver:Apartment Hunting Shaughnessy Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Apartments for rent in Shaughnessy Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Shaughnessy, Vancouver
              </LinkText>
              , are relatively limited due to the neighborhood’s predominantly
              single-family home landscape. However, the available apartments
              often feature spacious layouts and upscale finishes, catering to
              professionals and small families seeking a quiet, prestigious
              area. Renting an apartment in Shaughnessy offers the advantage of
              residing in a serene environment with close proximity to urban
              amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Houses for rent in Shaughnessy Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Shaughnessy, Vancouver
              </LinkText>
              , are ideal for families and individuals desiring spacious living
              in a historic and prestigious neighborhood. These homes often
              boast large lots, manicured gardens, and classic architectural
              designs. Renting a house in Shaughnessy provides the benefit of a
              serene environment, excellent schools, and easy access to both
              nature and city life.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Townhouses for rent in Shaughnessy Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Shaughnessy, Vancouver
              </LinkText>{' '}
              , offer a balance between apartment living and detached homes.
              These properties often provide multiple bedrooms and modern
              amenities, catering to professionals and families seeking a
              prestigious address. Renting a townhouse here allows residents to
              enjoy the community’s peaceful ambiance, reputable schools, and
              convenient access to shopping and recreational facilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Basements for rent in Shaughnessy Paragraph">
              Basements for rent in Shaughnessy, Vancouver, provide affordable
              housing options for students and young professionals. These units
              vary in size and amenities, often featuring private entrances and
              essential appliances. Renting a basement suite in this
              neighborhood offers the advantage of residing in a safe,
              family-friendly area with excellent transit connections.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Condos for rent Shaughnessy Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Shaughnessy, Vancouver
              </LinkText>
              , appeal to individuals and couples looking for modern living
              spaces with amenities like gyms and communal areas. These units
              often feature contemporary designs and are situated close to
              shopping centers and parks. Renting a condo here provides a
              low-maintenance lifestyle in a desirable neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Rooms for rent in Shaughnessy Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Shaughnessy, Vancouver
              </LinkText>{' '}
              , are ideal for students and single professionals seeking
              cost-effective housing. These accommodations typically involve
              shared common areas and provide a community living experience.
              Renting a room in this neighborhood allows for affordable living
              in a safe and attractive area.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.shaughnessy-vancouver:Short term rentals in Shaughnessy Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Shaughnessy, Vancouver
              </LinkText>
              , are suitable for tourists, business travelers, or individuals in
              transition. These accommodations range from private rooms to
              entire homes, offering flexibility and comfort. Renting short-term
              in this neighborhood provides a peaceful retreat while remaining
              close to Vancouver’s attractions and amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.shaughnessy-vancouver:Exploring Shaughnessy Paragraph">
          Shaughnessy is a serene and picturesque neighborhood nestled in the
          heart of Vancouver. Known for its winding, tree-lined streets and
          well-preserved heritage homes, the area exudes a sense of historic
          charm and exclusivity. Residents and visitors can enjoy numerous green
          spaces, such as the{' '}
          <LinkText href="https://vandusengarden.org/">
            VanDusen Botanical Garden
          </LinkText>
          , which offers 55 acres of themed gardens and walking trails. The
          neighborhood’s central location provides easy access to shopping
          districts like{' '}
          <LinkText href="https://www.southgranville.org/">
            South Granville
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://vancouver.ca/news-calendar/kerrisdale.aspx">
            Kerrisdale
          </LinkText>
          , as well as reputable schools and cultural landmarks, making it a
          highly desirable place to live.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.shaughnessy-vancouver:Things To Do Shaughnessy Paragraph">
          Shaughnessy offers a variety of activities and attractions catering to
          diverse interests. Nature enthusiasts can explore the{' '}
          <LinkText href="https://vandusengarden.org/">
            VanDusen Botanical Garden
          </LinkText>
          , a 55-acre oasis featuring themed gardens, walking trails, and a
          hedge maze. For those interested in history and architecture, a stroll
          through the neighborhood reveals numerous heritage homes showcasing
          early 20th-century designs. Art lovers can visit nearby galleries on{' '}
          <LinkText href="https://www.southgranville.org">
            South Granville
          </LinkText>
          , known as Gallery Row, which showcases regional and international
          talent. With its blend of natural beauty and urban amenities,
          Shaughnessy caters to both locals and tourists seeking a balanced
          lifestyle.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.shaughnessy-vancouver:Restaurants Shaughnessy Paragraph">
        <Paragraph>
          Shaughnessy Vancouver offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://shaughnessyrestaurant.com/">
            Shaughnessy Restaurant
          </LinkText>{' '}
          - Located within the VanDusen Botanical Garden, Shaughnessy Restaurant
          offers contemporary comfort food in an elegant garden setting. The
          menu features seasonal dishes prepared with locally sourced
          ingredients, providing a fresh and flavorful dining experience. With
          its floor-to-ceiling windows overlooking the garden, the ambiance is
          both serene and sophisticated, making it perfect for intimate dinners
          or special occasions. This restaurant caters to those seeking a
          tranquil dining experience amidst natural beauty.
        </Paragraph>
        <Paragraph>
          2) <LinkText href="https://fish-cafe.ca/">Fish Café</LinkText> - Fish
          Café is a cozy seafood restaurant known for its fresh catches and
          casual atmosphere. The menu offers a variety of dishes, from classic
          fish and chips to grilled salmon, all prepared with high-quality
          ingredients. The relaxed setting and friendly service make it a
          favorite among locals looking for a laid-back dining experience.
          Seafood enthusiasts will appreciate the straightforward, delicious
          offerings.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="https://adoniatea.com/">Adonia Tea House</LinkText>{' '}
          - Adonia Tea House offers a traditional British afternoon tea
          experience in a cozy and elegant setting. Guests can enjoy a selection
          of finely brewed teas accompanied by an assortment of finger
          sandwiches, scones, and pastries. The intimate ambiance, complete with
          vintage décor, makes it a perfect spot for leisurely afternoons and
          special occasions.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.shaughnessy-vancouver:Transportation Shaughnessy Paragraph">
          Getting around Shaughnessy, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Shaughnessy are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Shaughnessy offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.shaughnessy-vancouver:Where To Stay Shaughnessy Paragraph">
          Accommodation options in Shaughnessy, Vancouver cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Shaughnessy itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Shaughnessy
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Shaughnessy
